import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getLogger } from "@hoteltradergit/ht-telemetry-ui-lib";
import { REVENUE_THRESHOLD } from "../constants/recommendations";
import { BNBERRY_CARD_STATE } from "../components/performance/action-center/action-cards/bnberryConstants";

// The tz plugin requires utc; both are idempotent to re-extend.
dayjs.extend(utc);
dayjs.extend(timezone);

// LocalDate serializes as "YYYY-MM-DD"; anything else is not a date we can
// safely compare day-to-day.
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Today's calendar date as YYYY-MM-DD — at the hotel when its IANA zone is
 * known, else on the user's clock. An expiry date is the HOTEL's calendar
 * date, so judging it on the user's clock can be a day off either way (a
 * supplier in India sees a Vegas hotel's card die ~12h early). A blank or
 * unrecognised zone falls back to the user's clock rather than throwing —
 * same guard as PriceCheck's todayAtProperty.
 */
const todayFor = (hotelTimeZone) => {
  const zone = typeof hotelTimeZone === "string" ? hotelTimeZone.trim() : "";
  if (zone) {
    try {
      return dayjs().tz(zone).format("YYYY-MM-DD");
    } catch (error) {
      // A zone the runtime rejects means bad property data — report it to
      // Rapid7 so it gets fixed at the source, then fall through to the
      // user's clock so the cards still render.
      getLogger().error("filterExpiredV2Cards", {
        type: "Error",
        error_desc: `Unrecognised hotel timeZone "${zone}"; falling back to user clock for v2 expiry check`,
        error_stack: error?.stack ?? null,
        page_url: window.location.href,
      });
    }
  }
  return dayjs().format("YYYY-MM-DD");
};

/**
 * Drops v2 cards whose expiryDate is in the past (EO-1704).
 *
 * The v1 read filters expired recommendations in SQL (expiry_date >= CURDATE()
 * in every promo repository), but the v2 read only scopes to the latest run —
 * so when the pipeline stops re-running a property, its last run's cards keep
 * showing forever. This mirrors the v1 rule on the client: a card expiring
 * today still shows; it disappears the day after. "Today" is the hotel's
 * calendar day when `hotelTimeZone` is a usable IANA zone, the user's
 * otherwise — see todayFor.
 *
 * Comparison is lexicographic on the ISO "YYYY-MM-DD" strings, which avoids
 * `new Date("YYYY-MM-DD")`'s UTC-midnight parsing shifting the day in
 * west-of-UTC timezones. A card with no expiryDate — or one in a shape that
 * doesn't parse as a date — is kept: expiry is an optional column, and a
 * malformed value must not hide a live recommendation.
 */
export const filterExpiredV2Cards = (v2Data, hotelTimeZone) => {
  if (!Array.isArray(v2Data?.cards)) return v2Data;
  const today = todayFor(hotelTimeZone);
  return {
    ...v2Data,
    cards: v2Data.cards.filter((card) => {
      const expiry = card?.expiryDate;
      if (typeof expiry !== "string" || !ISO_DATE_RE.test(expiry)) return true;
      return expiry >= today;
    }),
  };
};

// Flatten the grouped PROMO results (each group is { type, data: [...] }) into a
// single list of promo recommendations.
const collectPromos = (promoData) =>
  (promoData || []).reduce((acc, group) => acc.concat(group?.data || []), []);

// v2 impact bands whose figures are the hotel's own revenue, and so may be totalled as-is.
const OWN_REVENUE_BANDS = new Set(["own_dollar", "own_pct"]);

/**
 * peer_proven's dollar figure, or null when its `amount` is not dollars.
 *
 * The guard exists because the field has NO consistent unit — same band, same key, two meanings:
 *   63029  amount 5765.0  "Comparable hotels ... book ~$5,765/mo through it"   -> dollars
 *   70531  amount 3357.0  "Comparable hotels ... book ~$3,357/mo through it"   -> dollars
 *   68805  amount   46.4  "NRF drives ~46% of production ... (~$3,415/mo avg)" -> PERCENT
 *
 * impact_json carries no unit field, so the copy is the only signal: the amount is dollars when
 * value_text actually quotes it as dollars. 68805's 46.4 fails that (its text quotes $3,415, a
 * different number) and so cannot be summed as $46.
 *
 * This is a guard against an upstream inconsistency, not a design. The real fix is for the
 * pipeline to put percentages in amount_pct, where every other band already puts them.
 */
const peerProvenDollars = (impact) => {
  const amount = Number(impact?.amount);
  if (!Number.isFinite(amount) || amount <= 0) return null;

  // en-US explicitly, NOT the host locale: this matcher is compared against backend copy, which
  // always groups with commas. Left to the browser, a de-DE user formats 5765 as "5.765", never
  // matches "$5,765/mo", and the card silently vanishes from the total for them alone.
  // Negative lookahead so an amount of 46 does not match "$46,000" in the copy.
  const quoted = Math.round(amount)
    .toLocaleString("en-US")
    .replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
  const isQuotedAsDollars = new RegExp(String.raw`\$${quoted}(?![\d,])`).test(
    String(impact?.value_text ?? "")
  );

  return isQuotedAsDollars ? amount : null;
};

/**
 * Roll up the revenue potential shown across the promo cards.
 *
 * Show absolute numbers whenever possible, percentage only as a fallback:
 * - When at least one card renders an absolute figure (>= REVENUE_THRESHOLD),
 *   show the total in USD — summing the underlying dollar value of both
 *   absolute and percentage cards ("$24,300/mth").
 * - When no absolute figure exists, fall back to the summed percentage of
 *   the percentage cards ("+12%/month").
 *
 * Returns a display-ready string or null when there is no revenue to show.
 */
export const computeBannerRevenue = (promoData, v2Data = null) => {
  const totals = { usd: 0, pct: 0, hasAbsolute: false, hasPercentage: false };

  collectPromos(promoData).forEach((promo) =>
    addContribution(
      totals,
      Number(promo?.expectedRevenueIncrease),
      Number(promo?.expectedRevenueIncreasePct)
    )
  );

  (v2Data?.cards || []).forEach((card) => {
    // impact_json's own `type` wins over the impact_band_type column, with the column as the
    // fallback when the blob has none.
    //
    // The blob is self-describing: its `type` describes the very amount/amount_pct fields read
    // below, and it is also what the CARD renders — LlmCard passes the column to ImpactBand only
    // to select the market_gap branch, while the label and value shown come from the blob. So
    // the banner has to follow the blob or it disagrees with the card in front of the supplier.
    //
    // The two do drift. Property 63029's alignment card arrives with column `peer_proven` and a
    // blob that is plainly own_pct (amount null, amount_pct 10.0, "~10% of your monthly revenue
    // lost to cancellations"). Resolving from the column sent it down the peer_proven path,
    // where it found no dollar amount and contributed nothing — while the card on screen showed
    // a 10% figure the banner had silently dropped.
    const band = card?.impact?.type ?? card?.impactBandType;

    if (OWN_REVENUE_BANDS.has(band)) {
      // own_* bands are this hotel's own money and go in as-is.
      addContribution(
        totals,
        Number(card?.impact?.amount),
        Number(card?.impact?.amount_pct)
      );
    } else if (band === "peer_proven") {
      // peer_proven counts too, but only its dollars — see peerProvenDollars for why the unit
      // has to be checked. Its amount_pct is null on every card seen, and a percentage of PEER
      // production is not a percentage of this hotel's revenue, so it is deliberately not read.
      const usd = peerProvenDollars(card?.impact);
      if (usd !== null) addContribution(totals, usd, Number.NaN);
    }
    // market_gap has no money at all (delta_pts is percentage POINTS of a share metric), and
    // `none` has no impact object. Neither contributes, so neither has a branch.
  });

  if (totals.hasAbsolute) {
    return `$${Math.round(totals.usd).toLocaleString()}/mth`;
  }
  if (totals.hasPercentage) {
    return `+${Math.round(totals.pct)}%/month`;
  }
  return null;
};

/**
 * One card's contribution, applying the absolute-over-percentage rule above. Both feeds go
 * through here so a property with v1 and v2 cards produces one figure rather than two competing
 * ones, and so v2 follows the same threshold v1 does.
 *
 * v2 supplies impact_json's amount/amount_pct. Cards that carry no money drop out here without
 * needing a filter: `none` cards have a null impact, and `market_gap` cards have both fields null.
 * A market gap is measured in percentage POINTS of a share metric — "48pts below comparable
 * hotels" is not 48% more revenue — so gap.delta_pts must never be treated as a revenue figure.
 */
const addContribution = (totals, usd, pct) => {
  const hasUsd = Number.isFinite(usd) && usd > 0;
  const hasPct = Number.isFinite(pct) && pct > 0;

  if (hasUsd) totals.usd += usd;

  if (hasUsd && usd >= REVENUE_THRESHOLD) {
    totals.hasAbsolute = true;
  } else if (hasPct) {
    totals.hasPercentage = true;
    totals.pct += pct;
  }
};

/**
 * Derive everything the banner/popover needs from the Action Center payload:
 * whether to render at all (only when real "main" cards exist, not fallback
 * cards), the revenue string, and whether a corridor card is present.
 *
 * `v2Data` (optional) is the Action Centre v2 feed. It counts towards both `show` and
 * `revenue`, the latter from impact_json's amount/amount_pct.
 *
 * Both impact labels count. "REVENUE AT STAKE" reads like a loss, but the banner's own copy is
 * "You're missing out on revenue" / "Boost revenue up to" — revenue lost to cancellations is
 * exactly that, and recovering it is as much an opportunity as closing a demand gap. The labels
 * describe framing, not sign.
 */
export const getBannerData = (data, v2Data = null) => {
  const corridorData = data?.results?.CORRIDOR || [];
  const corridorRequestData = data?.results?.CORRIDOR_REQUEST || [];
  const ariData = data?.results?.ARI || [];
  const promoData = data?.results?.PROMO || [];
  // A BnBerry card is a new-buyer demand opportunity treated like a corridor — but only in the
  // RECOMMENDATION (not-yet-opted-in) state. A PENDING card means the hotel already clicked
  // Opt-in Now, so it must NOT drive the "Unlock demand: New Buyer Invitations" banner or the
  // "New buyer invitations waiting" popover (there is nothing left to act on).
  const bnberry = (data?.results?.BNBERRY || [])[0] || null;
  const hasBnberryOpportunity =
    bnberry?.cardState === BNBERRY_CARD_STATE.RECOMMENDATION;

  const promoCount = promoData.reduce(
    (count, group) => count + (group?.data?.length || 0),
    0
  );

  const hasCorridor = corridorData.length > 0 || hasBnberryOpportunity;

  const hasMainCards =
    hasCorridor ||
    corridorRequestData.length > 0 ||
    ariData.length > 0 ||
    promoCount > 0 ||
    (v2Data?.cards?.length || 0) > 0;

  return {
    show: hasMainCards,
    revenue: computeBannerRevenue(promoData, v2Data),
    hasCorridor,
  };
};
